import { isDefined } from "@clipboard-health/util-ts";
// eslint-disable-next-line no-restricted-imports
import { Box, Stack, Tab, Tabs } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { Pill } from "@src/appV2/redesign/system/ui-components";
import { transformResponseIntoInterview } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { useCreateOrGetPlacementCandidate } from "../../PlacementCandidate/api/useCreateOrGetPlacementCandidate";
import { useGetEasySchedulingPlacementApplications } from "../api/useGetEasySchedulingPlacementApplications";
import { useGetEasySchedulingPlacements } from "../api/useGetEasySchedulingPlacements";
import { useInterviewAttendanceOutcome } from "../hooks/useInterviewAttendanceOutcome";

interface JobsApplicationsTabsProps {
  showApplications: boolean;
  onChange: (showApplications: boolean) => void;
}

export function JobsApplicationsTabs({ showApplications, onChange }: JobsApplicationsTabsProps) {
  const worker = useDefinedWorker();
  const { data: interviewsDataWithPages } = useInterviewAttendanceOutcome({
    workerId: worker.userId,
    withDismissed: false,
  });
  const interviews = interviewsDataWithPages?.pages.flatMap((page) =>
    page.data.map((interview) =>
      transformResponseIntoInterview(interview, page.included ?? [], worker.tmz ?? "")
    )
  );

  const { data: placementCandidate } = useCreateOrGetPlacementCandidate({
    workerId: worker.userId,
  });

  const isPlacementApplicationsMigrationEnabled = useCbhFlag(
    CbhFeatureFlag.PLACEMENT_APPLICATIONS_MIGRATION,
    { defaultValue: false }
  );

  const isLegacyQueryEnabled =
    !isPlacementApplicationsMigrationEnabled &&
    isDefined(worker.userId) &&
    isDefined(placementCandidate?.id);
  const isPlacementApplicationsQueryEnabled =
    isPlacementApplicationsMigrationEnabled && isDefined(worker.userId);

  // Fetch easy scheduling placements - count from first page (200 items)
  const { data: legacyEasySchedulingData } = useGetEasySchedulingPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate?.id ?? "",
      shouldLogEvent: false,
    },
    {
      enabled: isLegacyQueryEnabled,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Count accepted applications the worker can self-book an interview for. The new endpoint
  // does not accept filter[applicationMethod] server-side, so we filter on meta.allowRequestInterview
  // (the authoritative signal, present on both endpoints) client-side. Flattening across all loaded
  // pages lets the count reflect pagination driven by AppliedPlacementListView (same query key).
  const { data: easySchedulingApplicationsData } = useGetEasySchedulingPlacementApplications(
    {
      workerId: worker.userId,
    },
    {
      enabled: isPlacementApplicationsQueryEnabled,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  const easySchedulingCount = isPlacementApplicationsMigrationEnabled
    ? (easySchedulingApplicationsData?.pages
        .flatMap((page) => page.data)
        .filter((application) => application.meta.allowRequestInterview).length ?? 0)
    : (legacyEasySchedulingData?.pages[0]?.data.length ?? 0);
  const totalBadgeCount = (interviews?.length ?? 0) + easySchedulingCount;

  return (
    <Tabs
      value={showApplications ? "applications" : "jobs"}
      variant="fullWidth"
      sx={{
        width: "100%",
        border: "none",
        borderRadius: 0,
        borderBottom: (theme) => `3px solid ${theme.palette.background.secondary}`,
        "& .MuiTabs-indicator": {
          backgroundColor: (theme) => theme.palette.text.primary,
          height: 3,
          bottom: 0,
          top: "auto",
        },
        "& .MuiTab-root": {
          minHeight: 45,
          fontWeight: (theme) => theme.typography.fontWeightMedium,
          color: (theme) => theme.palette.text.disabled,
          backgroundColor: "transparent",
        },
        "& .MuiTab-root.Mui-selected": {
          color: (theme) => theme.palette.text.primary,
        },
      }}
      onChange={(_, newValue) => {
        onChange(newValue === "applications");
      }}
    >
      <Tab value="jobs" label="Open Staff Jobs" />
      <Tab
        value="applications"
        label={
          <Stack alignItems="center" direction="row" spacing={2}>
            <Box>My Jobs</Box>
            {totalBadgeCount > 0 ? (
              <Pill
                label={totalBadgeCount}
                size="small"
                sx={{
                  backgroundColor: (theme) => theme.palette.error.main,
                  color: (theme) => theme.palette.error.contrastText,
                  padding: 0,
                  minHeight: 20,
                  minWidth: 20,
                  height: "max-content",
                  borderRadius: 10,
                }}
              />
            ) : undefined}
          </Stack>
        }
      />
    </Tabs>
  );
}
